import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const DEFAULT_LOCALE = 'en-US';

export interface AvailableLanguage {
  localeCode: string;
  localeName: string;
  isDefault: boolean;
  hasTranslations?: boolean;
}

export interface PropertyFormatting {
  currencyCode?: string;
  currencySymbol?: string;
  currencyDigits?: number;
  numberFormat?: {
    decimalSeparator?: string;
    thousandsSeparator?: string;
    grouping?: string;
  };
  localeCode?: string;
  measurementSystem?: string;
  unitsOfMeasure?: import('utils/formatting/units').PropertyUnitsOfMeasure;
  unitOfMeasureForArea?: string;
  unitOfMeasureForWeight?: string;
  unitOfMeasureForDistance?: string;
}

type LocaleState = {
  localeCode: string;
  currencyCode: string;
  propertyFormatting: PropertyFormatting | null;
  propertyCountryCode?: string;
  availableLanguages: AvailableLanguage[];
  propertyId: number | null;
};

const initialState: LocaleState = {
  // Intentionally unset so property default language can be applied when
  // available (instead of hard-biasing to en-US).
  localeCode: '',
  currencyCode: '',
  propertyFormatting: null,
  propertyCountryCode: '',
  availableLanguages: [],
  propertyId: null,
};

const localeSlice = createSlice({
  name: 'locale',
  initialState,
  reducers: {
    /**
     * Set the active locale. Callers are responsible for validating the value
     * via validateLocale() before dispatching — see utils/locale.ts.
     */
    setLocale: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.localeCode = action.payload;
    },

    setCurrency: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.currencyCode = action.payload;
    },

    setPropertyFormatting: (state, action: PayloadAction<PropertyFormatting | null>) => {
      // eslint-disable-next-line no-param-reassign
      state.propertyFormatting = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.currencyCode = action.payload?.currencyCode ?? '';
    },

    setPropertyCountryCode: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.propertyCountryCode = action.payload;
    },

    setAvailableLanguages: (state, action: PayloadAction<AvailableLanguage[]>) => {
      // eslint-disable-next-line no-param-reassign
      state.availableLanguages = action.payload;
    },

    /**
     * Record the property currently being served so locale state can be
     * invalidated when the user navigates to a different property.
     */
    setPropertyId: (state, action: PayloadAction<number | null>) => {
      // eslint-disable-next-line no-param-reassign
      state.propertyId = action.payload;
    },
  },
});

export const {
  setLocale,
  setCurrency,
  setPropertyFormatting,
  setPropertyCountryCode,
  setAvailableLanguages,
  setPropertyId,
} = localeSlice.actions;

/**
 * @deprecated Use setPropertyId. Kept temporarily for any external callers;
 * remove once all call sites are updated.
 */
export const setLocalePropertyId = setPropertyId;

export default localeSlice.reducer;
